import { clearCache } from "@optisewer/library";
import { getBrowserLanguage, setDialog, setSession, setSettings, setSnackbar, stringAvatar } from "@shared/library";
import importer from "@shared/library/dist/methods/importer";
import axios, { AxiosError } from "axios";
import React from "react";
import { useNavigate } from 'react-router-dom';
import packageJson from "../../../../../package.json";
import { languages } from "../../../../language";
import { config } from "../../../../static/config";
import './Header.scss';
//#endregion
//#region COMPONENTS
import { Divider, Tooltip } from "@mui/material";
import Avatar from '@mui/material/Avatar';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import GestButton from "@shared/library/dist/components/gest/GestInputs/button/GestButton";
import ContextMenuContent from "../../../gest/ContextMenuContent/ContextMenuContent";
import Popover from "../../../gest/Popover/Popover";
//#endregion
//#region ICONS
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import LogoutIcon from '@mui/icons-material/Logout';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import ExitLogo from '../../../../assets/images/icons/exit.png';
import { ReactComponent as HeaderLogo } from '../../../../assets/images/logo_dark.svg';
//CONTEXTS
import { CntxConfig } from "../../../Optisewer";

const Header = (props: { isOpened: boolean; }) => {
    //CONTEXTS USE
    const cntxConfig = React.useContext(CntxConfig)!;

    //#region STATIC DATA
    const lang = languages(cntxConfig.settings?.lang);
    const isOpened = props.isOpened;
    const navigate = useNavigate();
    const availableLangs = [
        { id: "it", name: "italian" },
        { id: "en", name: "english", iconName: "gb" },
        { id: "fr", name: "french" },
        { id: "ro", name: "romanian" },
    ];
    //#endregion

    //#region STATES
    const anchorRef = React.useRef<HTMLButtonElement>(null);
    //FLAGS
    const [infoOpened, setInfoOpened] = React.useState(false);
    const [logoutLoading, setLogoutLoading] = React.useState(false);
    //#endregion

    const demoFlag = cntxConfig.session?.userData?.demo_data_enabled;
    const isDemoMode = demoFlag === true || demoFlag === 'true';

    //#region METHODS

    //#region API CALLS
    const logoutAPI = () => {
        setLogoutLoading(true);
        axios.delete(`${config.api.internal}/Auth/logout`, { withCredentials: true })
            .then(() =>
                /** The shared IndexedDB cache is keyed per provider_host, not per user - clear it before navigating away, the next login may be a different host entirely. */
                clearCache().then(() => setSession(cntxConfig))
            )
            .catch((err: AxiosError) => setSnackbar(cntxConfig, { message: lang.error_logout, severity: "error" }))
            .finally(() => setLogoutLoading(false));
    };

    const updateLanguageAPI = (langCode: string) =>
        axios.patch(`${config.api.internal}/Account/Language/ChangeTo/${langCode}`, undefined, { withCredentials: true })
            .then(() => {
                let settingsLS = JSON.parse(localStorage.getItem('settings') || '{}');

                settingsLS.lang = langCode;
                setSettings(cntxConfig, settingsLS);
                localStorage.setItem('settings', JSON.stringify(settingsLS));
            })
            .catch((err: AxiosError) => setSnackbar(cntxConfig, { message: lang.error_saving_language, severity: "error" }));
    //#endregion

    const handleClose = (event: Event | React.SyntheticEvent) => {
        if (
            anchorRef.current &&
            anchorRef.current.contains(event.target as HTMLElement)
        ) {
            return;
        }

        setInfoOpened(false);
    };

    const openLogoutDialog = () => setDialog(cntxConfig, {
        content:
            <div className="flex-column" style={{ width: '400px', textAlign: 'center' }}>
                <img height="100" width="100" style={{ margin: 'auto' }} src={ExitLogo} alt="" />
                {lang.really_want_logout}
            </div>,
        footer:
            <GestButton
                icon={<LogoutIcon />}
                className="btn-danger"
                loading={logoutLoading}
                label={lang.logout}
                onclick={logoutAPI}
            />
    });
    //#endregion

    return (
        <nav id="gestNavbar" className={`fullw justify-content-between align-items-center ${isOpened ? 'opened' : ''}`}>
            <div id="logos" className="align-items-center">
                <Tooltip title={"Version " + packageJson.version}>
                    <div
                        className="logoBox"
                        onClick={() => navigate("/")}
                        onAuxClick={event => {
                            if (event.button === 1) {
                                window.open(`/`, '_blank');
                            }
                        }}
                    >
                        <HeaderLogo />
                    </div>
                </Tooltip>
            </div>

            <div className="utils">
                {isDemoMode && <span className="demoModeChip">{lang.demo}</span>}

                <div className="align-items-center">
                    {cntxConfig.session?.userData?.role.toLowerCase() === "superadmin" &&
                        <GestButton
                            iconBtn
                            tooltip="Admin Panel"
                            icon={<ManageAccountsIcon />}
                            onclick={() => navigate("/administration-panel")}
                        />
                    }

                    <GestButton
                        iconBtn
                        tooltip="Logout"
                        icon={<LogoutIcon />}
                        onclick={openLogoutDialog}
                    />
                </div>

                <Divider orientation="vertical" />

                <div style={{ width: '100%' }}>
                    <Popper
                        open={infoOpened}
                        anchorEl={anchorRef.current}
                        role={undefined}
                        placement="bottom-start"
                        transition
                        disablePortal
                    >
                        {({ TransitionProps }) => (
                            <Grow
                                {...TransitionProps}
                                style={{
                                    transformOrigin: 'right top',
                                }}
                            >
                                <Paper>
                                    <ClickAwayListener onClickAway={handleClose}>
                                        <></>
                                    </ClickAwayListener>
                                </Paper>
                            </Grow>
                        )}
                    </Popper>
                </div>

                <Popover
                    variant="ghost"
                    anchor="bottom-right"
                    arrow={true}
                    overflow={true}
                    placeholder={
                        <div className='account'>
                            <Avatar {...stringAvatar(`${cntxConfig.session?.userData!.firstname} ${cntxConfig.session?.userData!.lastname}`)} />
                            <div className="anag">
                                <h4>{`${cntxConfig.session?.userData!.firstname} ${cntxConfig.session?.userData!.lastname}`}</h4>
                                <h5>@{cntxConfig.session?.userData!.username}</h5>
                            </div>
                        </div>
                    }
                >
                    <ContextMenuContent
                        anchor="right"
                        options={[
                            {
                                leftIcon: <PublicRoundedIcon style={{ fill: "var(--primary)" }} />,
                                label: lang.language,
                                subs: availableLangs.map(l => ({
                                    label: lang.universal.languages[l.name as keyof typeof lang.universal.languages],
                                    onClick: () => updateLanguageAPI(l.id),
                                    leftIcon: <img style={{}} src={importer.flag.require(l.iconName ?? l.id, "svg")} alt="" loading="lazy" />,
                                    rightIcon: cntxConfig.settings?.lang === l.id || (!cntxConfig.settings?.lang && getBrowserLanguage() === l.id) ? <CheckRoundedIcon /> : <></>,
                                }))
                            }
                        ]}
                    />
                </Popover>
            </div>
        </nav>
    );
};
export default React.memo(Header);